import {
    GeneratePanelV2,
    type MenuItem,
    usePageState,
} from "@hg/recall-frame"
import cls from "classnames"
import { useCallback, useMemo, useState } from "react"

import StrokeText from "@/components/StrokeText"
import { useCheckLogin, useOwnTeam } from "@/hooks"
import { i18n, type RecallTextPath } from "@/i18n"
import {
    GlobalStage,
    GlobalView,
    useGlobalStore,
    useSetGlobalView,
    useTransitionStore,
} from "@/store"
import RuleModal from "../RuleModal"
import PoolModal from "./PoolModal"
import { Progress } from "./Progress"
import { Record } from "./Record"
import Share from "./Share"
import { TaskList } from "./TaskList"

import styles from "./index.module.scss"

const removeParam = () => {
    const url = new URL(window.location.href)
    url.searchParams.delete("page") // 删除 `page` 参数
    window.history.replaceState({}, document.title, url.toString()) // 更新地址栏
}

const Extra: React.FC = () => {
    const { setGlobalStage } = useGlobalStore()
    const set = useSetGlobalView()
    const fadeTransition = useTransitionStore((state) => state.fadeTransition)

    const handleClick = useCallback(() => {
        fadeTransition(() => {
            setGlobalStage(GlobalStage.LOADING)
            set(GlobalView.MAIN)
            removeParam()
        })
    }, [fadeTransition, setGlobalStage, set])

    return (
        <button
            type="button"
            className={styles.extraButton}
            onClick={handleClick}
        >
            <div className={styles.icon} data-type="back" />
            <div className={styles.textWrapper}>
                <StrokeText
                    text={i18n.getRecall("panel.back")}
                    fontFamily="Georgia-Bold"
                    fontSize={30}
                    fillColor="#ffffff"
                    borderWidth={4}
                    borderColor="#564426"
                    offsetY={0}
                    padding={4}
                    baselineAligned
                    className={styles.text}
                />
            </div>
        </button>
    )
}

const LOGIN_REQUIRED_MENUS = ["progress", "record"]

interface MenuButtonProps {
    mid: string
    onClick: () => void
}

const MenuButton: React.FC<MenuButtonProps> = (props) => {
    const { mid, onClick } = props

    const checkLogin = useCheckLogin()
    const { activeMenu } = usePageState()

    const active = useMemo(() => activeMenu === mid, [activeMenu, mid])

    const title = useMemo(() => {
        return i18n.getRecall(`menu.${mid}` as RecallTextPath)
    }, [mid])

    const handleClick = useCallback(() => {
        if (LOGIN_REQUIRED_MENUS.includes(mid) && !checkLogin()) return
        onClick()
    }, [mid, checkLogin, onClick])

    return (
        <>
            <button
                type="button"
                className={cls(styles.menuButton, active && styles.active)}
                onClick={handleClick}
            >
                <div className={styles.icon} data-type={mid} />
                {title.split("").map((char, index) => (
                    <StrokeText
                        key={index}
                        text={char}
                        fontFamily="Georgia-Bold"
                        fontSize={36}
                        fillColor="#fffbf1"
                        borderWidth={3}
                        borderColor="#614518"
                        offsetY={0}
                        padding={3}
                        baselineAligned
                        className={styles.text}
                    />
                ))}
            </button>
            <div className={styles.divider} />
        </>
    )
}

interface PanelProps {
    onRefreshDraw?: () => void
}

export const Panel: React.FC<PanelProps> = ({ onRefreshDraw }) => {
    const [poolOpen, setPoolOpen] = useState(false)
    const [ruleOpen, setRuleOpen] = useState(false)
    const [shareOpen, setShareOpen] = useState(false)
    const [taskRefreshKey, setTaskRefreshKey] = useState(0)
    const { setActiveMenu } = usePageState()

    const { mutate: mutateOwnTeam } = useOwnTeam()

    const refreshTasks = useCallback(() => {
        setTaskRefreshKey((k) => k + 1)
        onRefreshDraw?.()
        mutateOwnTeam()
    }, [onRefreshDraw, mutateOwnTeam])

    const openProgress = useCallback(() => {
        setActiveMenu("progress")
    }, [setActiveMenu])

    const menus = useMemo(
        () =>
            [
                {
                    id: "task",
                    type: "view",
                    render: () => (
                        <TaskList
                            onOpenShare={() => setShareOpen(true)}
                            onOpenProgress={openProgress}
                            refreshKey={taskRefreshKey}
                            onClaimComplete={refreshTasks}
                        />
                    ),
                },
                {
                    id: "progress",
                    type: "view",
                    render: () => <Progress />,
                },
                {
                    id: "record",
                    type: "view",
                    render: () => <Record />,
                },
                {
                    id: "pool",
                    type: "action",
                    onClick: () => setPoolOpen(true),
                },
                {
                    id: "rule",
                    type: "action",
                    onClick: () => setRuleOpen(true),
                },
            ] as MenuItem[],
        [taskRefreshKey],
    )

    return (
        <>
            <GeneratePanelV2
                backgroundCls={styles.background}
                panelCls={styles.panel}
                menuCls={styles.menu}
                contentCls={styles.content}
                renderMenuBtn={({ key, mid, onClick }) => (
                    <MenuButton key={key} {...{ mid, onClick }} />
                )}
                menus={menus}
                defaultMenu="task"
                renderShare={() => (
                    <Share open={shareOpen} onChange={setShareOpen} onShareComplete={refreshTasks} />
                )}
                renderExtra={() => <Extra />}
            />
            <PoolModal open={poolOpen} onCancel={() => setPoolOpen(false)} />
            <RuleModal open={ruleOpen} onCancel={() => setRuleOpen(false)} />
        </>
    )
}

export default Panel
